import { Injectable } from '@angular/core';

import { ListResponse, ListResponseCountOptional } from '../../models/list-response';
import { StaffCheckInResult } from '../../models/check-in';
import {
  Customer,
  CustomerAlert,
  CustomerBooking,
  CustomerBookingFilters,
  CustomerCheckIn,
  CustomerClimberAppTokenResponse,
  CustomerCredit,
  CustomerDocument,
  CustomerEmergencyContact,
  CustomerEventVoucher,
  CustomerFilters,
  CustomerGuestPassUsage,
  CustomerMedicalNotes,
  CustomerNote,
  CustomerPayment,
  CustomerPaymentMethod,
  CustomerPaymentMethodType,
  CustomerProficiency,
  CustomerLatestCheckIn,
  CustomerProfileDetails, CustomerRecentActivityResult,
  CustomerRelation,
  CustomerReservationPart,
  CustomerReservationPartFilters,
  CustomerReservationTimeScope,
  CustomerSearchResult,
  CustomerSharedPass,
  CustomerTag,
  GuestPass,
  GuestPassEligibility,
  GuestPassFilters,
  IncomingPendingCustomerRelation,
  MemberAncillaryRevenueByCustomerFilters,
  MemberAncillaryRevenueByCustomerItem,
  PendingCustomerRelation,
  PendingCustomerRelationAction,
  PendingCustomerRelationRequestView,
  RootCustomerEventVoucher,
  RootCustomerEventVoucherFilters,
  UnmatchedLinkRequestLog,
} from '../../models/customer';

import { CustomerMembership, CustomerMembershipCustomer, MembershipPerCustomerAddOn } from '../../models/membership';
import { CustomerEntryPass } from '../../models/entry-pass';
import { ApiService } from '../api/api.service';
import { ActivityLogEntry, ActivityLogEntryFilters } from '../../models/activity-log';
import { BackendEmail, BackendEmailFilters } from '../../models/backend-email';
import { AccountService } from '../account/account.service';
import { batchAll, getHttpParamsForFilters, HttpUrlEncodingCodec, propExists } from '../../utils';
import { HttpParams } from '@angular/common/http';
import { MembershipDiscountType, MembershipPurchaseType } from '../../models/membership-enums';

export interface FeeWithTax {
  base: string;
  tax: string;
  total: string;
}

export interface FeeInfo {
  fee_amount: string;
  fee_is_less_than_minimum: boolean;
}

export interface BulkSetMembershipEndDatesFailure {
  customer_membership_id: number;
  error: string;
}

export interface BulkSetMembershipEndDatesResponse {
  succeeded_customer_membership_ids: number[];
  failed: BulkSetMembershipEndDatesFailure[];
}

@Injectable({
  providedIn: 'root'
})
export class CustomerService {
  /**
   * A service providing access to customers and their subcollections via the
   * API.
   */

  private static readonly BULK_RETRIEVE_CHUNK_SIZE = 200;
  private static readonly BULK_RETRIEVE_CONCURRENCY = 4;
  private static readonly BULK_RETRIEVE_HARD_CAP = 10000;

  constructor(
    private apiService: ApiService,
    private accountService: AccountService
  ) {}

  /**
   * Get a page of customers from the API.
   *
   * @param pageNumber - The requested page number.
   * @param pageSize - The requested number of customers per page.
   * @param filters - The set of filters to apply to the customers.
   * @param isOperational - Indicates to the API server that this is an
   *     "operational" query, which will use the primary database (as opposed
   *     to the replica).
   * @return The list of customers.
   */
  getCustomers(pageNumber?: number,
               pageSize?: number,
               filters?: CustomerFilters,
               isOperational?: boolean): Promise<ListResponse<Customer>> {
    // Don't provide the search filter until at least three characters have
    // been typed.
    const filtersCopy = {...filters};
    if (propExists(filtersCopy.search) && filtersCopy.search.length < 3) {
      delete filtersCopy.search;
    }

    return this.apiService.getCollection<Customer, CustomerFilters>(
      'customers/',
      pageNumber,
      pageSize,
      filtersCopy,
      isOperational
    );
  }

  searchCustomers(pageNumber?: number,
                  pageSize?: number,
                  filters?: CustomerFilters,
                  isOperational?: boolean,
                  includeCount?: boolean): Promise<ListResponseCountOptional<CustomerSearchResult>> {
    let params = getHttpParamsForFilters(filters);
    if ((pageNumber === undefined) || (pageNumber === null)) {
      pageNumber = 1;
    }
    params = params.set('page', pageNumber.toString());
    if (pageSize) {
      params = params.set('page_size', pageSize.toString());
    }
    if (isOperational) {
      params = params.set('is_operational', 'true');
    }
    if (includeCount) {
      params = params.set('include_count', 'true');
    }

    return this.apiService.getFromAPI<ListResponseCountOptional<CustomerSearchResult>>(
      'customer-search/',
      params
    );
  }

  async bulkRetrieveCustomersById(customerIds: number[]): Promise<ListResponseCountOptional<Customer>> {
    /**
     * Batch the requested customer IDs in groups of 200, to avoid exceeding nginx's max URL length.
     */
    const unique = Array.from(new Set(customerIds));
    if (unique.length === 0) {
      return { results: [], count: 0 };
    }
    if (unique.length > CustomerService.BULK_RETRIEVE_HARD_CAP) {
      throw new Error(`bulkRetrieveCustomersById: ${unique.length} exceeds ${CustomerService.BULK_RETRIEVE_HARD_CAP}-id cap`);
    }

    const chunks: number[][] = [];
    for (let i = 0; i < unique.length; i += CustomerService.BULK_RETRIEVE_CHUNK_SIZE) {
      chunks.push(unique.slice(i, i + CustomerService.BULK_RETRIEVE_CHUNK_SIZE));
    }

    const fetchChunk = (ids: number[]): Promise<ListResponseCountOptional<Customer>> => {
      let params = new HttpParams({ encoder: new HttpUrlEncodingCodec() });
      params = params.set('customer_ids', ids.join(','));
      return this.apiService.getFromAPI<ListResponseCountOptional<Customer>>(
        'customer-bulk-retrieve-by-id/',
        params
      );
    };

    const responses = await batchAll(
      CustomerService.BULK_RETRIEVE_CONCURRENCY, chunks, fetchChunk,
    );

    const merged: Customer[] = [];
    let totalCount = 0;
    let anyCountPresent = false;
    for (const r of responses) {
      merged.push(...r.results);
      if (r.count !== undefined) {
        totalCount += r.count;
        anyCountPresent = true;
      }
    }
    return { results: merged, count: anyCountPresent ? totalCount : undefined };
  }

  getRecentlyActiveCustomers(locationId: number | null): Promise<ListResponseCountOptional<CustomerRecentActivityResult>> {
    const params: any = {};
    if (locationId !== null) {
      params.location_id = locationId
    }
    return this.apiService.getFromAPI<ListResponseCountOptional<CustomerRecentActivityResult>>(
      'recently-active-customers/',
      params
    );
  }

  /**
   * Get an individual customer from the API.
   *
   * @param id - The database ID of the customer.
   * @return The customer.
   */
  getCustomer(id: number): Promise<Customer> {
    return this.apiService.getItem<Customer>(
      'customers/',
      id
    );
  }

  /**
   * Get the profile details for an individual customer from the API. These are
   * additional fields needed to display the profile page which are too
   * computationally-expensive to include in the standard customer object.
   *
   * @param id - The database ID of the customer.
   * @return The customer.
   */
  getCustomerProfileDetails(id: number): Promise<CustomerProfileDetails> {
    return this.apiService.getItem<CustomerProfileDetails>(
      'gym-endpoints/customer-profile-details/',
      id
    );
  }

  /**
   * The timestamp of a customer's most recent check-in, via its own
   * single-column endpoint: an open profile polls it every few seconds (PRO-1449).
   */
  getCustomerLatestCheckIn(id: number): Promise<CustomerLatestCheckIn> {
    return this.apiService.getItem<CustomerLatestCheckIn>(
      'gym-endpoints/customer-latest-check-in/',
      id,
      true
    );
  }

  /**
   * Update a customer by making a PATCH request to the API.
   *
   * @param customer - The customer, with updated values.
   * @param profileImageFileData - The profile image file data, if a new
   *     profile image is to be uploaded.
   * @return The updated customer returned from the API.
   */
  updateCustomer(customer: Customer,
                 profileImageFileData: File | null,
                 profileImageBase64Data: string | null): Promise<Customer> {
    let updatePromise = this.apiService.patchToAPI<Customer>(
      customer.url,
      {
        email: customer.email,
        first_name: customer.first_name,
        last_name: customer.last_name,
        preferred_name: customer.preferred_name,
        birthday: customer.birthday,
        address_postcode: customer.address_postcode,
        address_street_1: customer.address_street_1,
        address_street_2: customer.address_street_2,
        address_city: customer.address_city,
        address_state: customer.address_state,
        address_country: customer.address_country,
        government_id: customer.government_id,
        telephone: customer.telephone,
        gender: customer.gender,
        pronouns: customer.pronouns,
        barcode: customer.barcode,
        has_opted_in_to_marketing: customer.has_opted_in_to_marketing,
        home_location_id: customer.home_location_id,
        custom_field_values: customer.custom_field_values
      }
    );

    // If a profile image was provided, upload that after the initial PATCH.
    if (profileImageFileData) {
      updatePromise = updatePromise.then(updatedCustomer => {
        const profileImageFormData = new FormData();
        profileImageFormData.append(
          'profile_image',
          profileImageFileData,
          profileImageFileData.name
        );
        return this.apiService.patchToAPI<Customer>(
          updatedCustomer.url,
          profileImageFormData,
          true
        );
      });
    }
    if (profileImageBase64Data) {
      updatePromise = updatePromise.then(updatedCustomer => {
        return this.apiService.postToAPI<Customer>(
          'customers/' + updatedCustomer.id.toString() + '/add-profile-image-base64/',
          {
            profile_image_base64: profileImageBase64Data
          }
        );
      });
    }

    return updatePromise;
  }

  updateCustomerBarcode(customer: Customer): Promise<Customer> {
    return this.apiService.patchToAPI<Customer>(
      customer.url,
      {barcode: customer.barcode}
    );
  }

  /**
   * Get a page of customers with their parents and guardians from the API.
   *
   * This uses the same filters and serialization as getCustomers, but returns
   * the parents and guardians of the filtered customers, in addition to the
   * filtered customers themselves.
   *
   * @param pageNumber - The requested page number.
   * @param pageSize - The requested number of customers per page.
   * @param filters - The set of filters to apply to the customers.
   * @return The list of customers.
   */
  getCustomerParentsAndGuardians(pageNumber?: number,
                                 pageSize?: number,
                                 filters?: CustomerFilters): Promise<ListResponse<Customer>> {
    // Don't provide the search filter until at least three characters have
    // been typed.
    const filtersCopy = {...filters};
    if (propExists(filtersCopy.search) && filtersCopy.search.length < 3) {
      delete filtersCopy.search;
    }

    return this.apiService.getCollection<Customer, CustomerFilters>(
      'customer-parents-and-guardians/',
      pageNumber,
      pageSize,
      filtersCopy,
      false
    );
  }

  /**
   * Get a page of customers' parents and guardians from the API.
   *
   * This uses the same filters and serialization as getCustomers, but returns
   * the parents and guardians of the filtered customers, rather than the
   * filtered customers themselves.
   *
   * @param pageNumber - The requested page number.
   * @param pageSize - The requested number of customers per page.
   * @param filters - The set of filters to apply to the customers.
   * @return The list of customers.
   */
  getCustomerParentsAndGuardiansOnly(pageNumber?: number,
                                     pageSize?: number,
                                     filters?: CustomerFilters): Promise<ListResponse<Customer>> {
    // Don't provide the search filter until at least three characters have
    // been typed.
    const filtersCopy = {...filters};
    if (propExists(filtersCopy.search) && filtersCopy.search.length < 3) {
      delete filtersCopy.search;
    }

    return this.apiService.getCollection<Customer, CustomerFilters>(
      'customer-parents-and-guardians-only/',
      pageNumber,
      pageSize,
      filtersCopy,
      false
    );
  }

  /**
   * Get a page of customers with their check-in counts in a given date range,
   * optionally filtered by that same check-in count, from the API.
   *
   * This uses the same filters and serialization as getCustomers, but with the
   * addition of the check-in count in range functionality.
   *
   * Added filters:
   * - check_in_range_start - The start of the date range for counting check-ins.
   * - check_in_range_end - The end of the date range for counting check-ins.
   * - check_in_range_count_min - The minimum number of check-in counts in the
   *   range by which to filter customers (optional).
   * - check_in_range_count_max - The maximum number of check-in counts in the
   *   range by which to filter customers (optional).
   *
   * @param pageNumber - The requested page number.
   * @param pageSize - The requested number of customers per page.
   * @param filters - The set of filters to apply to the customers.
   * @return The list of customers.
   */
  getCustomersWithCheckInCountInRange(pageNumber?: number,
                                      pageSize?: number,
                                      filters?: CustomerFilters,
                                      isOperational?: boolean): Promise<ListResponse<Customer>> {
    // Don't provide the search filter until at least three characters have
    // been typed.
    const filtersCopy = {...filters};
    if (propExists(filtersCopy.search) && filtersCopy.search.length < 3) {
      delete filtersCopy.search;
    }

    return this.apiService.getCollection<Customer, CustomerFilters>(
      'customers-with-check-in-count-in-range/',
      pageNumber,
      pageSize,
      filtersCopy,
      isOperational
    );
  }

  /***
   * Send an invite email to the customer.
   */
  sendCustomerInvite(customer: Customer): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/send-customer-invite/' + customer.id.toString() + '/',
      {}
    ).then();
  }

  /**
   * Add a tag to a customer.
   */
  addCustomerTag(customerId: number, tagName: string): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/add-customer-tag/' + customerId.toString() + '/',
      {tag_name: tagName}
    ).then();
  }

  /**
   * Remove a tag from a customer.
   */
  removeCustomerTag(customerId: number, tagName: string): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/remove-customer-tag/' + customerId.toString() + '/',
      {tag_name: tagName}
    ).then();
  }

  /**
   * Send the given customer an Allthenticate invite by hand.
   */
  sendAllthenticateInvite(customerId: number): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/send-allthenticate-invite/' + customerId.toString() + '/',
      {}
    ).then();
  }

  /**
   * Remove the given customer from the organization's Allthenticate group,
   * revoking their door access.
   */
  removeAllthenticateAccess(customerId: number): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/remove-allthenticate-access/' + customerId.toString() + '/',
      {}
    ).then();
  }

  /**
   * Get a token allowing one-off login to the Climber App as the given customer.
   */
  getCustomerClimberAppToken(customerId: number): Promise<CustomerClimberAppTokenResponse> {
    return this.apiService.postToAPI<CustomerClimberAppTokenResponse>(
      'gym-endpoints/get-climber-app-token/' + customerId.toString() + '/',
      {}
    );
  }

  /**
   * Get a token allowing one-off login to the Climber App as a staff user.
   */
  getStaffClimberAppToken(): Promise<CustomerClimberAppTokenResponse> {
    return this.apiService.postToAPI<CustomerClimberAppTokenResponse>(
      'gym-endpoints/get-climber-app-staff-token/',
      {}
    );
  }

  getRootCustomerEventVouchers(pageNumber?: number,
                               pageSize?: number,
                               filters?: RootCustomerEventVoucherFilters): Promise<ListResponse<RootCustomerEventVoucher>> {
    return this.apiService.getCollection<RootCustomerEventVoucher, RootCustomerEventVoucherFilters>(
      'event-vouchers/',
      pageNumber,
      pageSize,
      filters
    );
  }

  /**
   * Get a page of event vouchers for the given customer from the API.
   */
  getCustomerEventVouchers(customerId: number,
                           pageNumber?: number,
                           pageSize?: number): Promise<ListResponse<CustomerEventVoucher>> {
    return this.apiService.getCollectionNoFilters<CustomerEventVoucher>(
      'customers/' + customerId.toString() + '/event-vouchers/',
      pageNumber,
      pageSize
    );
  }

  /**
   * Post a new customer event voucher to the API.
   */
  addCustomerEventVoucher(customerId: number,
                          voucher: CustomerEventVoucher): Promise<CustomerEventVoucher> {
    return this.apiService.postToAPI<CustomerEventVoucher>(
      'customers/' + customerId.toString() + '/event-vouchers/',
      voucher
    );
  }

  /**
   * Update an event voucher by making a PUT request to the API.
   *
   * @param voucher - The event voucher, with updated values.
   * @return The updated event voucher returned from the API.
   */
  updateCustomerEventVoucher(voucher: CustomerEventVoucher): Promise<CustomerEventVoucher> {
    return this.apiService.putToAPI<CustomerEventVoucher>(
      voucher.url,
      voucher
    );
  }

  /**
   * Get a page of credits for the given customer from the API.
   */
  getCustomerCredits(customerId: number,
                     pageNumber?: number,
                     pageSize?: number): Promise<ListResponse<CustomerCredit>> {
    return this.apiService.getCollectionNoFilters<CustomerCredit>(
      'customers/' + customerId.toString() + '/credits/',
      pageNumber,
      pageSize
    );
  }

  /**
   * Post a new customer credit to the API.
   */
  addCustomerCredit(customerId: number,
                    credit: CustomerCredit): Promise<CustomerCredit> {
    return this.apiService.postToAPI<CustomerCredit>(
      'customers/' + customerId.toString() + '/credits/',
      credit
    );
  }

  /**
   * Update a credit by making a PUT request to the API.
   *
   * @param credit - The credit, with updated values.
   * @return The updated credit returned from the API.
   */
  updateCustomerCredit(credit: CustomerCredit): Promise<CustomerCredit> {
    return this.apiService.putToAPI<CustomerCredit>(
      credit.url,
      credit
    );
  }

  /**
   * Get a page of emergency contacts for the given customer from the API.
   */
  getCustomerEmergencyContacts(customerId: number,
                               pageNumber?: number,
                               pageSize?: number): Promise<ListResponse<CustomerEmergencyContact>> {
    return this.apiService.getCollectionNoFilters<CustomerEmergencyContact>(
      'customers/' + customerId.toString() + '/emergency-contacts/',
      pageNumber,
      pageSize
    );
  }

  addCustomerEmergencyContact(customerId: number,
                              emergencyContact: CustomerEmergencyContact): Promise<CustomerEmergencyContact> {
    return this.apiService.postToAPI<CustomerEmergencyContact>(
      'customers/' + customerId.toString() + '/emergency-contacts/',
      emergencyContact
    );
  }

  updateCustomerEmergencyContact(emergencyContact: CustomerEmergencyContact): Promise<CustomerEmergencyContact> {
    return this.apiService.putToAPI<CustomerEmergencyContact>(
      emergencyContact.url,
      emergencyContact
    );
  }

  removeCustomerEmergencyContact(emergencyContact: CustomerEmergencyContact): Promise<CustomerEmergencyContact> {
    return this.apiService.patchToAPI<CustomerEmergencyContact>(
      emergencyContact.url,
      {is_removed: true}
    );
  }

  /**
   * Get a page of medical notes for the given customer from the API.
   */
  getCustomerMedicalNotes(customerId: number,
                          pageNumber?: number,
                          pageSize?: number): Promise<ListResponse<CustomerMedicalNotes>> {
    return this.apiService.getCollectionNoFilters<CustomerMedicalNotes>(
      'customers/' + customerId.toString() + '/medical-notes/',
      pageNumber,
      pageSize
    );
  }

  /**
   * Post a new set of customer medical notes to the API.
   *
   * @param customerId - The customer for which to create notes.
   * @param note - The newly-created notes returned from the API.
   */
  addCustomerMedicalNotes(customerId: number,
                          note: CustomerMedicalNotes): Promise<CustomerMedicalNotes> {
    return this.apiService.postToAPI<CustomerMedicalNotes>(
      'customers/' + customerId.toString() + '/medical-notes/',
      note
    );
  }

  /**
   * Update a customer's medical notes by making a PUT request to the API.
   *
   * @param notes - The notes, with updated values.
   * @return The updated notes returned from the API.
   */
  updateCustomerMedicalNotes(notes: CustomerMedicalNotes): Promise<CustomerMedicalNotes> {
    return this.apiService.putToAPI<CustomerMedicalNotes>(
      notes.url,
      notes
    );
  }

  /**
   * Remove a customer's medical notes by making a DELETE request to the API.
   *
   * @param notes - The notes to remove.
   */
  removeCustomerMedicalNotes(notes: CustomerMedicalNotes): Promise<void> {
    return this.apiService.deleteFromAPI<any>(notes.url).then();
  }

  /**
   * Get a page of notes for the given customer from the API.
   */
  getCustomerNotes(customerId: number,
                   pageNumber?: number,
                   pageSize?: number): Promise<ListResponse<CustomerNote>> {
    return this.apiService.getCollectionNoFilters<CustomerNote>(
      'customers/' + customerId.toString() + '/notes/',
      pageNumber,
      pageSize
    );
  }

  /**
   * Post a new customer note to the API.
   *
   * @param customerId - The customer for which to create a note.
   * @param note - The newly-created note returned from the API.
   */
  addCustomerNote(customerId: number,
                  note: CustomerNote): Promise<CustomerNote> {
    return this.apiService.postToAPI<CustomerNote>(
      'customers/' + customerId.toString() + '/notes/',
      note
    );
  }

  /**
   * Update a customer note by making a PUT request to the API.
   *
   * @param note - The note, with updated values.
   * @return The updated note returned from the API.
   */
  updateCustomerNote(note: CustomerNote): Promise<CustomerNote> {
    return this.apiService.putToAPI<CustomerNote>(
      note.url,
      note
    );
  }

  /**
   * Remove a customer note by making a PATCH request to the API setting its
   * is_removed value to true.
   *
   * @param note - The note to remove.
   * @return The updated note returned from the API (should have is_removed
   *     equal to true).
   */
  removeCustomerNote(note: CustomerNote): Promise<CustomerNote> {
    return this.apiService.patchToAPI<CustomerNote>(
      note.url,
      {is_removed: true}
    );
  }

  /**
   * Get a page of alerts for the given customer from the API.
   */
  getCustomerAlerts(customerId: number,
                    pageNumber?: number,
                    pageSize?: number): Promise<ListResponse<CustomerAlert>> {
    return this.apiService.getCollectionNoFilters<CustomerAlert>(
      'customers/' + customerId.toString() + '/alerts/',
      pageNumber,
      pageSize
    );
  }

  /**
   * Post a new customer alert to the API.
   *
   * @param customerId - The customer for which to create a alert.
   * @param alert - The newly-created alert returned from the API.
   */
  addCustomerAlert(customerId: number,
                   alert: CustomerAlert): Promise<CustomerAlert> {
    if (alert.expiry_date === '') {
      alert.expiry_date = null;
    }

    return this.apiService.postToAPI<CustomerAlert>(
      'customers/' + customerId.toString() + '/alerts/',
      alert
    );
  }

  /**
   * Remove a customer alert by making a PATCH request to the API setting its
   * is_removed value to true.
   *
   * @param alert - The alert to remove.
   * @return The updated alert returned from the API (should have is_removed
   *     equal to true).
   */
  removeCustomerAlert(alert: CustomerAlert): Promise<CustomerAlert> {
    return this.apiService.patchToAPI<CustomerAlert>(
      alert.url,
      {is_removed: true}
    );
  }

  /**
   * Get a page of documents for the given customer from the API.
   */
  getCustomerDocuments(customerId: number,
                       pageNumber?: number,
                       pageSize?: number): Promise<ListResponse<CustomerDocument>> {
    return this.apiService.getCollectionNoFilters<CustomerDocument>(
      'customers/' + customerId.toString() + '/documents/',
      pageNumber,
      pageSize
    );
  }

  /**
   * Get a page of relations for the given customer from the API.
   */
  getCustomerRelations(customerId: number,
                       pageNumber?: number,
                       pageSize?: number): Promise<ListResponse<CustomerRelation>> {
    return this.apiService.getCollectionNoFilters<CustomerRelation>(
      'customers/' + customerId.toString() + '/relations/',
      pageNumber,
      pageSize
    );
  }

  /**
   * Post a new customer relation to the API.
   */
  addCustomerRelation(customerId: number,
                      relation: CustomerRelation): Promise<CustomerRelation> {
    return this.apiService.postToAPI<CustomerRelation>(
      'customers/' + customerId.toString() + '/relations/',
      relation
    );
  }

  /**
   * Delete a customer relation by making a DELETE request to the API.
   *
   * @param relationUrl - The API URL of the customer relation to delete.
   * @return A promise which resolved when the customer relation is deleted.
   */
  deleteCustomerRelation(relationUrl: string): Promise<void> {
    return this.apiService.deleteFromAPI<any>(
      relationUrl
    ).then();
  }

  /**
   * Get a page of check-ins for the given customer from the API.
   */
  getCustomerCheckIns(customerId: number,
                      pageNumber?: number,
                      pageSize?: number): Promise<ListResponse<CustomerCheckIn>> {
    return this.apiService.getCollectionNoFilters<CustomerCheckIn>(
      'customers/' + customerId.toString() + '/check-ins/',
      pageNumber,
      pageSize
    );
  }

  /**
   * Get a page of guest pass usages for the given customer from the API.
   */
  getCustomerGuestPassUsages(customerId: number,
                             pageNumber?: number,
                             pageSize?: number): Promise<ListResponse<CustomerGuestPassUsage>> {
    return this.apiService.getCollectionNoFilters<CustomerGuestPassUsage>(
      'customers/' + customerId.toString() + '/guest-pass-usages/',
      pageNumber,
      pageSize
    );
  }

  /**
   * Get a page of shared passes for the given customer from the API.
   */
  getCustomerSharedPasses(customerId: number,
                          pageNumber?: number,
                          pageSize?: number): Promise<ListResponse<CustomerSharedPass>> {
    return this.apiService.getCollectionNoFilters<CustomerSharedPass>(
      'customers/' + customerId.toString() + '/shared-passes/',
      pageNumber,
      pageSize
    );
  }

  /**
   * Check a customer in.
   *
   * @param customerId - The ID of the customer to check in.
   * @param bypassAlerts - Whether to bypass check in alerts (such as expired
   *   waiver) when checking in.
   * @param freeEntryReason - A string explaining why the customer was granted
   *   free entry. If this is non-null (including the empty string), then the
   *   customer will be granted free entry. Otherwise, the customer's membership
   *   or entry pass will be used.
   */
  checkInCustomer(customerId: number,
                  bypassAlerts: boolean,
                  freeEntryReason?: string): Promise<StaffCheckInResult> {
    const postArguments: any = {};
    if (this.accountService.getDefaultLocation()) {
      postArguments.location_id = this.accountService.getDefaultLocation().id;
    }
    if (bypassAlerts) {
      postArguments.bypass_alerts = 'true';
    }
    if (freeEntryReason !== null) {
      postArguments.free_entry_reason = freeEntryReason;
    }

    return this.apiService.postToAPI<StaffCheckInResult>(
      'gym-endpoints/customer-check-in/' + customerId.toString() + '/',
        postArguments
    );
  }

  /**
   * Check a customer in using a certain entry pass (which may belong to a
   * different customer, in which case the pass would be shared).
   *
   * @param customerId - The ID of the customer to check in.
   * @param passId - The ID of the entry pass to use for check in.
   * @param bypassAlerts - Whether to bypass check in alerts (such as expired
   *   waiver) when checking in.
   */
  checkInCustomerViaEntryPass(customerId: number,
                              passId: number,
                              bypassAlerts: boolean): Promise<StaffCheckInResult> {
    const postArguments: any = {};
    if (this.accountService.getDefaultLocation()) {
      postArguments.location_id = this.accountService.getDefaultLocation().id;
    }
    if (bypassAlerts) {
      postArguments.bypass_alerts = 'true';
    }

    return this.apiService.postToAPI<StaffCheckInResult>(
      'gym-endpoints/customer-check-in-pass/' + customerId.toString() + '/' + passId.toString() + '/',
        postArguments
    );
  }

  /**
   * Check a customer in using a different customer's guest pass.
   *
   * @param checkingInCustomerId - The ID of the customer to check in.
   * @param guestPassId - The ID of the guest pass to use.
   * @param bypassAlerts - Whether to bypass check in alerts (such as expired
   *   waiver) when checking in.
   */
  checkInCustomerViaGuestPass(checkingInCustomerId: number,
                              guestPassId: number,
                              bypassAlerts: boolean): Promise<StaffCheckInResult> {
    const postArguments: any = {
      customer_id: checkingInCustomerId,
      guest_pass_id: guestPassId
    };
    if (this.accountService.getDefaultLocation()) {
      postArguments.location_id = this.accountService.getDefaultLocation().id;
    }
    if (bypassAlerts) {
      postArguments.bypass_alerts = 'true';
    }

    return this.apiService.postToAPI<StaffCheckInResult>(
      'gym-endpoints/customer-check-in-guest-pass/',
        postArguments
    );
  }

  /**
   * Check a customer in via an association. This will use entry passes owned by
   * the association.
   *
   * @param checkingInCustomerId - The ID of the customer to check in.
   * @param associationId - The ID of the association through which to check the
   *   customer in.
   * @param bypassAlerts - Whether to bypass check in alerts (such as expired
   *   waiver) when checking in.
   * @param freeEntryReason - A string explaining why the customer was granted
   *   free entry. If this is non-null (including the empty string), then the
   *   customer will be granted free entry.
   */
  checkInCustomerViaAssociation(checkingInCustomerId: number,
                                associationId: number,
                                bypassAlerts: boolean,
                                freeEntryReason: string | null): Promise<void> {
    const postArguments: any = {};
    if (this.accountService.getDefaultLocation()) {
      postArguments.location_id = this.accountService.getDefaultLocation().id;
    }
    if (bypassAlerts) {
      postArguments.bypass_alerts = 'true';
    }
    if (freeEntryReason !== null) {
      postArguments.free_entry_reason = freeEntryReason;
    }

    return this.apiService.postToAPI<any>(
      'gym-endpoints/customer-check-in-association/' +
          checkingInCustomerId.toString() + '/' + associationId.toString() + '/',
        postArguments
    ).then();
  }

  /**
   * Get a page of memberships for the given customer from the API.
   */
  getCustomerMemberships(customerId: number,
                         pageNumber?: number,
                         pageSize?: number): Promise<ListResponse<CustomerMembership>> {
    return this.apiService.getCollectionNoFilters<CustomerMembership>(
      'customers/' + customerId.toString() + '/memberships/',
      pageNumber,
      pageSize
    );
  }

  /**
   * Get a page of entry passes for the given customer from the API.
   */
  getCustomerEntryPasses(customerId: number,
                         pageNumber?: number,
                         pageSize?: number): Promise<ListResponse<CustomerEntryPass>> {
    return this.apiService.getCollectionNoFilters<CustomerEntryPass>(
      'customers/' + customerId.toString() + '/entry-passes/',
      pageNumber,
      pageSize
    );
  }

  /**
   * Post a new customer entry pass to the API.
   */
  addCustomerEntryPass(customerId: number, entryPass: CustomerEntryPass): Promise<CustomerEntryPass> {
    if (!entryPass.expiration_date) {
      delete entryPass.expiration_date;
    }

    return this.apiService.postToAPI<CustomerEntryPass>(
      'customers/' + customerId.toString() + '/entry-passes/',
      {
        entry_pass_id: entryPass.entry_pass_id,
        number_of_uses_total: entryPass.number_of_uses_total,
        number_of_uses_remaining: entryPass.number_of_uses_remaining,
        expiration_date: entryPass.expiration_date,
        owner_association_id: entryPass.owner_association_id,
        all_customer_ids: entryPass.all_customer_ids
      }
    );
  }

  /**
   * Update an entry pass by making a PUT request to the API.
   *
   * @param entryPass - The entry pass, with updated values.
   * @return The updated entry pass returned from the API.
   */
  updateCustomerEntryPass(entryPass: CustomerEntryPass): Promise<CustomerEntryPass> {
    if (entryPass.expiration_date === '') {
      entryPass.expiration_date = null;
    }

    return this.apiService.patchToAPI<CustomerEntryPass>(
      entryPass.url,
      {
        number_of_uses_total: entryPass.number_of_uses_total,
        number_of_uses_remaining: entryPass.number_of_uses_remaining,
        expiration_date: entryPass.expiration_date
      }
    );
  }

  updateCustomerEntryPassLinkedCustomers(entryPass: CustomerEntryPass): Promise<CustomerEntryPass> {
    return this.apiService.patchToAPI<CustomerEntryPass>(
      entryPass.url,
      {
        all_customer_ids: entryPass.all_customer_ids
      }
    );
  }

  /**
   * Get a page of payments for the given customer from the API.
   */
  getCustomerPayments(customerId: number,
                      pageNumber?: number,
                      pageSize?: number): Promise<ListResponse<CustomerPayment>> {
    return this.apiService.getCollectionNoFilters<CustomerPayment>(
      'customers/' + customerId.toString() + '/payments/',
      pageNumber,
      pageSize
    );
  }

  /**
   * Get a page of proficiencies for the given customer from the API.
   */
  getCustomerProficiencies(customerId: number,
                           pageNumber?: number,
                           pageSize?: number): Promise<ListResponse<CustomerProficiency>> {
    return this.apiService.getCollectionNoFilters<CustomerProficiency>(
      'customers/' + customerId.toString() + '/proficiencies/',
      pageNumber,
      pageSize
    );
  }

  /**
   * Grant a proficiency to a customer by making a request to the API.
   */
  grantCustomerProficiency(customerId: number,
                           proficiencyId: number,
                           expirationYears?: number,
                           expirationDate?: string): Promise<void> {
    const postArguments = {
      proficiency_id: proficiencyId
    };
    if (expirationDate) {
      // @ts-ignore
      postArguments.expiration_date = expirationDate;
    } else if (expirationYears) {
      // @ts-ignore
      postArguments.expiration_years = expirationYears;
    }

    return this.apiService.postToAPI<any>(
      'gym-endpoints/grant-customer-proficiency/' + customerId.toString() + '/',
      postArguments
    ).then();
  }

  /**
   * Update the expiration date of a customer's proficiency by making a request
   * to the API.
   */
  updateCustomerProficiencyExpiration(customerProficiencyId: number,
                                      expirationYears?: number,
                                      expirationDate?: string): Promise<void> {
    const postArguments = {};
    if (expirationDate) {
      // @ts-ignore
      postArguments.expiration_date = expirationDate;
    } else if (expirationYears) {
      // @ts-ignore
      postArguments.expiration_years = expirationYears;
    }

    return this.apiService.postToAPI<any>(
      'gym-endpoints/update-customer-proficiency-expiration/' + customerProficiencyId.toString() + '/',
      postArguments
    ).then();
  }

  /**
   * Delete a customer proficiency by making a POST request to the API.
   */
  removeCustomerProficiency(customerProficiencyId: number): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/remove-customer-proficiency/' + customerProficiencyId.toString() + '/',
      {}
    ).then();
  }

  /**
   * A page of the customer's reservation parts. Nonpayment-cancelled ones are
   * hidden unless `includeNonpaymentCancelled`; the API defaults `timeScope`
   * to the whole history.
   */
  getCustomerReservationParts(customerId: number,
                              pageNumber?: number,
                              pageSize?: number,
                              includeNonpaymentCancelled = false,
                              timeScope?: CustomerReservationTimeScope): Promise<ListResponse<CustomerReservationPart>> {
    return this.apiService.getCollection<CustomerReservationPart, CustomerReservationPartFilters>(
      'customers/' + customerId.toString() + '/reservation-parts/',
      pageNumber,
      pageSize,
      {
        include_nonpayment_cancelled: includeNonpaymentCancelled || undefined,
        time_scope: timeScope,
      }
    );
  }

  /**
   * A page of the bookings the customer made or is a participant in
   * (PRO-1338). Roster-generated and nonpayment-cancelled bookings are hidden
   * unless asked for.
   */
  getCustomerBookings(customerId: number,
                      pageNumber?: number,
                      pageSize?: number,
                      includeRosterBookings = false,
                      includeNonpaymentCancelled = false): Promise<ListResponse<CustomerBooking>> {
    return this.apiService.getCollection<CustomerBooking, CustomerBookingFilters>(
      'customers/' + customerId.toString() + '/bookings/',
      pageNumber,
      pageSize,
      {
        include_roster_bookings: includeRosterBookings || undefined,
        include_nonpayment_cancelled: includeNonpaymentCancelled || undefined,
      }
    );
  }

  /**
   * Get a page of activity log entries for the given customer from the API.
   */
  getCustomerActivityLog(customerId: number,
                         pageNumber?: number,
                         pageSize?: number,
                         filters?: ActivityLogEntryFilters): Promise<ListResponse<ActivityLogEntry>> {
    return this.apiService.getCollection<ActivityLogEntry, ActivityLogEntryFilters>(
      'customers/' + customerId.toString() + '/activity-log/',
      pageNumber,
      pageSize,
      filters
    );
  }

  /**
   * Get a page of backend emails sent to the given customer from the API.
   */
  getCustomerBackendEmails(customerId: number,
                           pageNumber?: number,
                           pageSize?: number,
                           filters?: BackendEmailFilters): Promise<ListResponse<BackendEmail>> {
    return this.apiService.getCollection<BackendEmail, BackendEmailFilters>(
      'backend-emails/',
      pageNumber,
      pageSize,
      { ...filters, customer_id: customerId }
    );
  }

  /**
   * Get a single backend email by ID, including its body fields.
   */
  getBackendEmail(id: number): Promise<BackendEmail> {
    return this.apiService.getItem<BackendEmail>('backend-emails/', id);
  }

  /**
   * Get a page of pending relations for the given customer from the API.
   */
  getPendingCustomerRelations(customerId: number,
                              pageNumber?: number,
                              pageSize?: number): Promise<ListResponse<PendingCustomerRelation>> {
    return this.apiService.getCollectionNoFilters<PendingCustomerRelation>(
      'customers/' + customerId.toString() + '/pending-relations/',
      pageNumber,
      pageSize
    );
  }

  /**
   * Get a page of incoming pending relations for the given customer from the
   * API. These are pending relations which were requested by the other
   * customer.
   */
  getIncomingPendingCustomerRelations(customerId: number,
                                      pageNumber?: number,
                                      pageSize?: number): Promise<ListResponse<IncomingPendingCustomerRelation>> {
    return this.apiService.getCollectionNoFilters<IncomingPendingCustomerRelation>(
      'customers/' + customerId.toString() + '/incoming-pending-relations/',
      pageNumber,
      pageSize
    );
  }

  /**
   * Get all non-hidden pending relations awaiting staff approval for the
   * user's org from the API.
   */
  getOrgPendingCustomerRelations(): Promise<PendingCustomerRelationRequestView[]> {
    return this.apiService.getFromAPI<PendingCustomerRelationRequestView[]>(
      'gym-endpoints/linking-account-requests/'
    );
  }

  /**
   * Get the list of unmatched link request logs for the user's org from
   * the API.
   */
  getOrgUnmatchedLinkRequestLogs(): Promise<UnmatchedLinkRequestLog[]> {
    return this.apiService.getFromAPI<UnmatchedLinkRequestLog[]>(
      'gym-endpoints/unmatched-minor-link-request-logs/'
    );
  }

  /**
   * Approve or deny a pending customer relation via a POST request to the API.
   */
  managePendingCustomerRelation(pendingRelationId: number,
                                action: PendingCustomerRelationAction): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/manage-pending-customer-relation/',
      {
        pending_relation_id: pendingRelationId,
        action
      }
    ).then();
  }

  /**
   * Immediately cancel a customer membership, optionally refunding its last
   * payment.
   *
   * @param membership - The membership to cancel.
   * @param cancellationEmailText - Text, in markdown format, to include in the
   *   cancellation email sent to the membership owner.
   * @param refundLastPayment - Whether to refund the last payment made on the
   *   membership.
   * @param refundCreditAmount - The amount of credit to grant the customer, as
   *   an alternate refund. Only applies if refundLastPayment is false.
   * @param reason - The provided reason for the cancellation, if required by
   *   the org's membership settings.
   */
  cancelMembershipImmediately(membership: CustomerMembership,
                              cancellationEmailText: string,
                              refundLastPayment: boolean,
                              refundCreditAmount: number,
                              reason?: string): Promise<void> {
    if (membership.purchase_type === MembershipPurchaseType.recurring) {
      const postArgs: any = {
        customer_membership_id: membership.id,
        cancellation_email_text: cancellationEmailText
      }

      if (refundLastPayment) {
        postArgs.refund_policy = 'refund_last_billed';
      } else if (refundCreditAmount > 0) {
        postArgs.refund_policy = 'apply_credit';
        postArgs.amount_to_refund = refundCreditAmount.toString();
      } else {
        postArgs.refund_policy = 'no_refund';
      }

      if ((reason !== undefined) && (reason !== null)) {
        postArgs.reason = reason;
      }

      return this.apiService.postToAPI<any>(
        'gym-endpoints/cancel-recurring-membership-immediately/',
        postArgs
      ).then();
    } else {
      const postArgs: any = {
        customer_membership_id: membership.id,
        cancellation_email_text: cancellationEmailText
      };

      // Mirrors the recurring branch: the prorated-credit choice must reach
      // the wire, or the cancellation succeeds and the customer gets nothing (PRO-1439).
      if (refundLastPayment) {
        postArgs.refund_policy = 'refund';
      } else if (refundCreditAmount > 0) {
        postArgs.refund_policy = 'apply_credit';
        postArgs.amount_to_refund = refundCreditAmount.toString();
      } else {
        postArgs.refund_policy = 'no_refund';
      }

      if ((reason !== undefined) && (reason !== null)) {
        postArgs.reason = reason;
      }

      return this.apiService.postToAPI<any>(
        'gym-endpoints/cancel-prepaid-membership/',
        postArgs
      ).then();
    }
  }

  /**
   * Cancel a membership paid for but never activated (awaiting a payment
   * method, PRO-1460). Unlike `cancelMembershipImmediately` the only question
   * is where the money goes: back to its source, or to account credit.
   */
  public cancelUnactivatedMembership(membership: CustomerMembership,
                                     refundToSource: boolean): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/cancel-unactivated-membership/',
      {
        customer_membership_id: membership.id,
        refund_destination: refundToSource ? 'source' : 'account_credit'
      }
    ).then();
  }

  /**
   * Schedule a customer membership to be cancelled on a future bill date.
   *
   * @param membership - The membership to cancel.
   * @param effectiveDate - The date the membership will be cancelled.
   * @param reason - The reason given by the customer for the cancellation.
   */
  cancelMembershipFutureBillDate(membership: CustomerMembership,
                                 effectiveDate: string,
                                 reason?: string): Promise<void> {
    if (membership.purchase_type !== MembershipPurchaseType.recurring) {
      return Promise.reject('Membership must be recurring');
    }

    const postArgs: any = {
      customer_membership_id: membership.id,
      membership_cancel_date: effectiveDate
    };

    if ((reason !== undefined) && (reason !== null)) {
      postArgs.reason = reason;
    }

    return this.apiService.postToAPI<any>(
      'gym-endpoints/cancel-recurring-membership-future-bill-date/',
      postArgs
    ).then();
  }

  /**
   * Schedule each recurring membership to end on the next bill date on or
   * after `effectiveDate`, replacing any existing end date. Per-row failures
   * are reported in the response, not thrown. `notes` is saved verbatim on
   * each ActivityLogEntry.
   */
  bulkSetMembershipEndDates(membershipIds: number[],
                            effectiveDate: string,
                            notes?: string): Promise<BulkSetMembershipEndDatesResponse> {
    const postArgs: any = {
      customer_membership_ids: membershipIds,
      effective_date: effectiveDate,
    };
    if (notes) {
      postArgs.notes = notes;
    }
    return this.apiService.postToAPI<BulkSetMembershipEndDatesResponse>(
      'gym-endpoints/bulk-set-recurring-membership-end-date/',
      postArgs,
    );
  }

  /**
   * Undo a scheduled cancellation of a customer membership.
   *
   * @param membership - The membership.
   * @param notes - Optional free-text notes saved verbatim on the resulting
   *   ActivityLogEntry.
   */
  undoCancelMembershipFutureBillDate(membership: CustomerMembership,
                                     notes?: string): Promise<void> {
    if (membership.purchase_type !== MembershipPurchaseType.recurring) {
      return Promise.reject('Membership must be recurring');
    }

    const postArgs: any = {
      customer_membership_id: membership.id,
    };
    if (notes) {
      postArgs.notes = notes;
    }

    return this.apiService.postToAPI<any>(
      'gym-endpoints/undo-cancel-recurring-membership-future-bill-date/',
      postArgs,
    ).then();
  }

  /**
   * Schedule a customer membership to be frozen on a given date.
   *
   * This may be in the past, in which case the freeze will be backdated.
   *
   * @param membership - The membership to freeze.
   * @param effectiveDate - The date the freeze will be applied.
   * @param unfreezeDate - The date the membership will be unfrozen (optional).
   * @param reason - The reason given by the customer for the freeze.
   */
  freezeMembershipOnDate(membership: CustomerMembership,
                         effectiveDate: string,
                         unfreezeDate: string | null,
                         reason: string | null): Promise<void> {
    if (membership.purchase_type !== MembershipPurchaseType.recurring) {
      return Promise.reject('Membership must be recurring');
    }

    const postArgs: any = {
      customer_membership_id: membership.id,
      membership_freeze_date: effectiveDate
    };

    if (propExists(reason)) {
      postArgs.reason = reason;
    }
    if (propExists(unfreezeDate)) {
      postArgs.membership_unfreeze_date = unfreezeDate;
    }

    return this.apiService.postToAPI<any>(
      'gym-endpoints/freeze-membership-on-date/',
      postArgs
    ).then();
  }

  /**
   * Schedule a member to be frozen on a given date.
   *
   * @param member - The member to freeze.
   * @param membership - The membership to which the member belongs.
   * @param effectiveDate - The date the freeze will be applied.
   * @param unfreezeDate - The date the member will be unfrozen (optional).
   * @param reason - The reason given by the customer for the freeze.
   */
  freezeMemberOnDate(member: CustomerMembershipCustomer,
                     membership: CustomerMembership,
                     effectiveDate: string,
                     unfreezeDate: string | null,
                     reason: string | null): Promise<void> {
    if (membership.purchase_type !== MembershipPurchaseType.recurring) {
      return Promise.reject('Membership must be recurring');
    }
    if (!membership.is_group) {
      return Promise.reject('Membership must be group');
    }

    const postArgs: any = {
      customer_membership_member_id: member.member_id,
      member_freeze_date: effectiveDate
    };

    if (propExists(reason)) {
      postArgs.reason = reason;
    }
    if (propExists(unfreezeDate)) {
      postArgs.member_unfreeze_date = unfreezeDate;
    }

    return this.apiService.postToAPI<any>(
      'gym-endpoints/freeze-member-on-date/',
      postArgs
    ).then();
  }

  freezeMembershipImmediately(membership: CustomerMembership,
                              reason?: string): Promise<void> {
    const postArgs: any = {
      customer_membership_id: membership.id
    };

    if ((reason !== undefined) && (reason !== null)) {
      postArgs.reason = reason;
    }

    return this.apiService.postToAPI<any>(
      'gym-endpoints/freeze-membership-immediately/',
      postArgs
    ).then();
  }

  getMembershipImmediateFreezeFee(membership: CustomerMembership): Promise<FeeWithTax> {
    return this.apiService.postToAPI<FeeWithTax>(
      'gym-endpoints/get-membership-immediate-freeze-fee/',
      {
        customer_membership_id: membership.id
      }
    );
  }

  getMembershipImmediateFreezeInvoice(membership: CustomerMembership,
                                      reason?: string): Promise<number> {
    const postArgs: any = {
      customer_membership_id: membership.id
    };

    if ((reason !== undefined) && (reason !== null)) {
      postArgs.reason = reason;
    }

    return this.apiService.postToAPI<number>(
      'gym-endpoints/get-membership-immediate-freeze-invoice/',
      postArgs
    );
  }

  /**
   * Undo a scheduled freezing/unfreezing of a customer membership.
   *
   * @param membership - The membership.
   */
  undoScheduledMembershipFreeze(membership: CustomerMembership): Promise<void> {
    if (membership.purchase_type !== MembershipPurchaseType.recurring) {
      return Promise.reject('Membership must be recurring');
    }

    return this.apiService.postToAPI<any>(
      'gym-endpoints/undo-scheduled-membership-freeze/',
      {
        customer_membership_id: membership.id
      }
    ).then();
  }

  /**
   * Undo a scheduled freezing/unfreezing of an individual member.
   *
   * @param member - The member.
   * @param unfreezeOnly - Whether to only remove the scheduled unfreeze (true)
   *   or both freeze and unfreeze (false).
   */
  undoScheduledMemberFreeze(member: CustomerMembershipCustomer,
                            unfreezeOnly: boolean): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/undo-scheduled-member-freeze/',
      {
        customer_membership_member_id: member.member_id,
        unfreeze_only: unfreezeOnly
      }
    ).then();
  }

  /**
   * Unfreeze a membership.
   *
   * @param membership - The membership to unfreeze.
   * @param unfreezeEmailText - Text, in markdown format, to include in the
   *   unfreeze email sent to the membership owner.
   */
  unFreezeMembership(membership: CustomerMembership,
                     unfreezeEmailText: string): Promise<void> {
    if (membership.purchase_type === MembershipPurchaseType.recurring) {
      return this.apiService.postToAPI<any>(
        'gym-endpoints/unfreeze-recurring-membership/',
        {
          customer_membership_id: membership.id,
          unfreeze_email_text: unfreezeEmailText
        }
      ).then();
    } else {
      return this.apiService.postToAPI<any>(
        'gym-endpoints/unfreeze-prepaid-membership/',
        {
          customer_membership_id: membership.id,
          unfreeze_email_text: unfreezeEmailText
        }
      ).then();
    }
  }

  /**
   * Schedule a membership to be unfrozen in the future.
   *
   * @param membership - The membership to unfreeze.
   * @param unfreezeDate - The date the membership will be unfrozen.
   */
  scheduleMembershipUnfreeze(membership: CustomerMembership,
                             unfreezeDate: string): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/unfreeze-membership-on-date/',
      {
        customer_membership_id: membership.id,
        membership_unfreeze_date: unfreezeDate
      }
    ).then();
  }

  /**
   * Schedule an individually-frozen member to be unfrozen in the future.
   *
   * @param member - The member to unfreeze.
   * @param unfreezeDate - The date the member will be unfrozen.
   */
  scheduleMemberUnfreeze(member: CustomerMembershipCustomer,
                         unfreezeDate: string): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/unfreeze-member-on-date/',
      {
        customer_membership_member_id: member.member_id,
        member_unfreeze_date: unfreezeDate
      }
    ).then();
  }

  /**
   * Unfreeze an individually-frozen member, effective immediately.
   *
   * @param member - The member to unfreeze.
   */
  unfreezeMemberImmediately(member: CustomerMembershipCustomer): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/unfreeze-member-immediately/',
      {
        customer_membership_member_id: member.member_id
      }
    ).then();
  }

  /**
   * Set a membership's start date. The membership must be scheduled to start
   * in the future.
   *
   * @param membership - The membership to edit.
   * @param startDate - The new start date, as an ISO-8601 date string.
   */
  editMembershipStartDate(membership: CustomerMembership,
                          startDate: string): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/edit-membership-start-date/',
      {
        customer_membership_id: membership.id,
        start_date: startDate
      }
    ).then();
  }

  editMembershipBillDate(membership: CustomerMembership,
                         newBillDate: string, notes: string): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/edit-membership-bill-date/',
      {
        customer_membership_id: membership.id,
        new_bill_date: newBillDate,
        notes
      }
    ).then();
  }

  /**
   * Update a membership's customer add-ons, effective on the membership's next
   * bill date.
   *
   * @param membershipId - The ID of the membership to update.
   * @param customerAddOns - A dictionary mapping each customer ID to its list
   *     of add-ons.
   * @return - A promise resolving to either a string, which is the ID of the
   *     invoice which must be paid to complete the update, or null if the
   *     update does not require payment.
   */
  editMembershipCustomerAddOnsNextBillDate(membershipId: number,
                                           customerAddOns: {[key: string]: MembershipPerCustomerAddOn[]}): Promise<void> {
    const customerAddOnIds = {};
    for (const customerId of Object.keys(customerAddOns)) {
      const thisCustomerAddOnIds = [];
      for (const addOn of customerAddOns[customerId]) {
        thisCustomerAddOnIds.push(addOn.id);
      }
      customerAddOnIds[customerId] = thisCustomerAddOnIds;
    }

    return this.apiService.postToAPI<any>(
      'gym-endpoints/edit-membership-customer-add-ons-next-bill-date/',
      {
        customer_membership_id: membershipId,
        customer_add_ons: customerAddOnIds
      }
    ).then();
  }

  /**
   * Update a membership's customer add-ons.
   *
   * @param membershipId - The ID of the membership to update.
   * @param customerAddOns - A dictionary mapping each customer ID to its list
   *     of add-ons.
   * @return - A promise resolving to either a string, which is the ID of the
   *     invoice which must be paid to complete the update, or null if the
   *     update does not require payment.
   */
  editMembershipCustomerAddOnsInvoice(membershipId: number,
                                      customerAddOns: {[key: string]: MembershipPerCustomerAddOn[]}): Promise<string> {
    const customerAddOnIds = {};
    for (const customerId of Object.keys(customerAddOns)) {
      const thisCustomerAddOnIds = [];
      for (const addOn of customerAddOns[customerId]) {
        thisCustomerAddOnIds.push(addOn.id);
      }
      customerAddOnIds[customerId] = thisCustomerAddOnIds;
    }

    return this.apiService.postToAPI<string>(
      'gym-endpoints/edit-membership-customer-add-ons-invoice/',
      {
        customer_membership_id: membershipId,
        customer_add_ons: customerAddOnIds
      }
    );
  }

  /**
   * Check the fee required to edit a membership's customer add-ons.
   *
   * @param membershipId - The ID of the membership to update.
   * @param customerAddOns - A dictionary mapping each customer ID to its list
   *     of add-ons.
   * @return - A promise resolving to info about the edit fee.
   */
  checkEditMembershipCustomerAddOnsInvoice(
    membershipId: number,
    customerAddOns: {[key: string]: MembershipPerCustomerAddOn[]}
  ): Promise<FeeInfo> {
    const customerAddOnIds = {};
    for (const customerId of Object.keys(customerAddOns)) {
      const thisCustomerAddOnIds = [];
      for (const addOn of customerAddOns[customerId]) {
        thisCustomerAddOnIds.push(addOn.id);
      }
      customerAddOnIds[customerId] = thisCustomerAddOnIds;
    }

    return this.apiService.postToAPI<FeeInfo>(
      'gym-endpoints/check-edit-membership-customer-add-ons-invoice/',
      {
        customer_membership_id: membershipId,
        customer_add_ons: customerAddOnIds
      }
    ).then();
  }

  /**
   * Reassign an individual membership (changing the member but not the owner).
   *
   * @param customerMembershipId - The membership to reassign.
   * @param customerId - The new member.
   */
  reassignCustomerMembership(customerMembershipId: number,
                             customerId: number): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/reassign-membership/',
      {
        customer_membership_id: customerMembershipId,
        customer_id: customerId
      }
    ).then();
  }

  /**
   * Edit a membership's recurring discount.
   */
  editCustomerMembershipRecurringDiscount(customerMembershipId: number,
                                          recurringDiscountType: MembershipDiscountType,
                                          recurringDiscountAmount: number,
                                          recurringDiscountAppliesToAddOns: boolean,
                                          recurringDiscountName: string,
                                          recurringDiscountAssociationId: number | null,
                                          recurringDiscountMembershipId: number | null): Promise<void> {
    const args: any = {
      customer_membership_id: customerMembershipId,
      recurring_discount_type: recurringDiscountType,
      recurring_discount_amount: recurringDiscountAmount,
      recurring_discount_applies_to_add_ons: recurringDiscountAppliesToAddOns,
      recurring_discount_name: recurringDiscountName
    };

    if (recurringDiscountAssociationId) {
      args.recurring_discount_association_id = recurringDiscountAssociationId;
    }
    if (recurringDiscountMembershipId) {
      args.recurring_discount_membership_id = recurringDiscountMembershipId;
    }

    return this.apiService.postToAPI<any>(
      'gym-endpoints/edit-membership-recurring-discount/',
      args
    ).then();
  }

  /**
   * Edit a membership's one-off discount.
   */
  editCustomerMembershipOneOffDiscount(customerMembershipId: number,
                                       oneOffDiscountType: MembershipDiscountType,
                                       oneOffDiscountAmount: number,
                                       oneOffDiscountAppliesToAddOns: boolean,
                                       oneOffDiscountName: string): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/edit-membership-one-off-discount/',
      {
        customer_membership_id: customerMembershipId,
        one_off_discount_type: oneOffDiscountType,
        one_off_discount_amount: oneOffDiscountAmount,
        one_off_discount_applies_to_add_ons: oneOffDiscountAppliesToAddOns,
        one_off_discount_name: oneOffDiscountName
      }
    ).then();
  }

  /**
   * Edit the number of days remaining on a prepaid membership (must be either active or frozen).
   *
   * @param membership - The membership to edit.
   * @param newDaysRemaining - The new number of days remaining.
   */
  editPrepaidMembershipNumberOfDaysRemaining(membership: CustomerMembership,
                                             newDaysRemaining: number): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/edit-prepaid-membership-days-remaining/',
      {
        customer_membership_id: membership.id,
        new_days_remaining: newDaysRemaining
      }
    ).then();
  }

  /**
   * Edit the membership's set of members, effective immediately.
   *
   * No prorated payment or refund will be made.
   */
  editGroupMembershipMembersImmediately(membership: CustomerMembership,
                                        customerIds: number[]): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/edit-group-membership-members-immediately/',
      {
        customer_membership_id: membership.id,
        customer_ids: customerIds
      }
    ).then();
  }

  addGroupMembershipMemberNextBillDate(membershipId: number,
                                       customerId: number): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/add-group-membership-member-next-bill-date/',
      {
        customer_membership_id: membershipId,
        customer_id: customerId
      }
    ).then();
  }

  cancelAddGroupMembershipMemberNextBillDate(membershipId: number,
                                             customerId: number): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/cancel-add-group-membership-member-next-bill-date/',
      {
        customer_membership_id: membershipId,
        customer_id: customerId
      }
    ).then();
  }

  removeGroupMembershipMemberNextBillDate(membershipId: number,
                                          customerId: number): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/remove-group-membership-member-next-bill-date/',
      {
        customer_membership_id: membershipId,
        customer_id: customerId
      }
    ).then();
  }

  cancelRemoveGroupMembershipMemberNextBillDate(membershipId: number,
                                                customerId: number): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/cancel-remove-group-membership-member-next-bill-date/',
      {
        customer_membership_id: membershipId,
        customer_id: customerId
      }
    ).then();
  }

  changeCustomerMembershipRoster(membershipId: number,
                                 rosterId: number): Promise<void> {
    return this.apiService.postToAPI<any>(
      'change-customer-membership-roster/',
      {
        customer_membership_id: membershipId,
        roster_id: rosterId
      }
    ).then();
  }

  /**
   * Transfer an entry pass, changing both the owner and the assigned customer.
   *
   * @param customerEntryPassId - The entry pass to reassign.
   * @param customerId - The new owner.
   */
  transferEntryPass(customerEntryPassId: number,
                    customerId: number): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/transfer-entry-pass/',
      {
        customer_entry_pass_id: customerEntryPassId,
        customer_id: customerId
      }
    ).then();
  }

  /**
   * Merge two customer accounts.
   */
  mergeCustomers(primaryCustomerId: number,
                 secondaryCustomerId: number): Promise<void> {
    const postArguments = {
      primary_customer_id: primaryCustomerId,
      secondary_customer_id: secondaryCustomerId
    };

    return this.apiService.postToAPI<any>(
      'gym-endpoints/merge-customers/',
      postArguments
    ).then();
  }

  /**
   * Generate a random unused customer barcode.
   */
  generateRandomCustomerBarcode(): Promise<string> {
    return this.apiService.getFromAPI<string>(
      'gym-endpoints/generate-random-customer-barcode/'
    );
  }

  /**
   * Create a new customer account via the API.
   *
   * @param firstName - The customer's first name.
   * @param lastName - The customer's last name.
   * @param birthday - The customer's birthday, as an ISO-8601 date.
   * @param email - The customer's email. May be left blank.
   * @param sendEmailInvite - Whether to send an email invite. Only has an
   *     effect if email is not blank.
   */
  createNewCustomer(firstName: string,
                    lastName: string,
                    birthday: string,
                    email: string,
                    sendEmailInvite: boolean): Promise<Customer> {
    const postArguments: any = {
      first_name: firstName,
      last_name: lastName,
      birthday
    };

    if (email !== '') {
      postArguments.email = email;
      postArguments.send_email_invite = sendEmailInvite;
    }

    return this.apiService.postToAPI<Customer>(
      'customers/',
      postArguments
    );
  }

  /**
   * Get a page of customer tags from the API.
   *
   * @param pageNumber - The requested page number.
   * @param pageSize - The requested number of tags per page.
   * @return The list of tags.
   */
  getCustomerTags(pageNumber?: number,
                  pageSize?: number): Promise<ListResponse<CustomerTag>> {
    return this.apiService.getCollectionNoFilters<CustomerTag>(
      'customer-tags/',
      pageNumber,
      pageSize
    );
  }

  setCustomerMailchimpGroups(customerId: number, groupIds: number[]): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/set-customer-mailchimp-groups/' + customerId.toString() + '/',
      {group_ids: groupIds}
    ).then();
  }

  getAssociationMembershipsCommonToCustomers(customerIds: number[]): Promise<{id: number, name: string}[]> {
    return this.apiService.getFromAPI<{id: number, name: string}[]>(
      'gym-endpoints/get-association-memberships-common-to-customers/' + customerIds.join(',') + '/'
    );
  }

  getMembershipsCommonToCustomers(customerIds: number[]): Promise<{id: number, name: string}[]> {
    return this.apiService.getFromAPI<{id: number, name: string}[]>(
      'gym-endpoints/get-memberships-common-to-customers/' + customerIds.join(',') + '/'
    );
  }

  downloadPrintableCustomerBarcode(customer: Customer): Promise<void> {
    return this.apiService.downloadFromApi(
      'gym-endpoints/get-printable-customer-barcode/' + customer.id.toString() + '/',
      'barcode-' + customer.first_name + '-' + customer.last_name + '.pdf'
    );
  }

  getMemberAncillaryRevenueByCustomer(
      pageNumber: number,
      pageSize: number,
      filters: MemberAncillaryRevenueByCustomerFilters): Promise<ListResponse<MemberAncillaryRevenueByCustomerItem>> {
    return this.apiService.getCollection<MemberAncillaryRevenueByCustomerItem, MemberAncillaryRevenueByCustomerFilters>(
      'member-ancillary-revenue-by-customer/',
      pageNumber,
      pageSize,
      filters,
      false
    );
  }

  getGuestPasses(pageNumber: number,
                 pageSize: number,
                 filters: GuestPassFilters,
                 isOperational: boolean): Promise<ListResponse<GuestPass>> {
    return this.apiService.getCollection<GuestPass, GuestPassFilters>(
      'guest-passes/',
      pageNumber,
      pageSize,
      filters,
      isOperational
    )
  }

  addGuestPasses(ownerId: number,
                 numPasses: number,
                 eligibility: GuestPassEligibility,
                 availableStartingAt: string,
                 expiresAt: string,
                 name: string): Promise<void> {
    const body: any = {
      owner_id: ownerId,
      num_passes: numPasses,
      eligibility
    };
    if (availableStartingAt !== '') {
      body.available_starting_at = availableStartingAt;
    }
    if (expiresAt !== '') {
      body.expires_at = expiresAt;
    }
    if (name !== '') {
      body.name = name;
    }

    return this.apiService.postToAPI<GuestPass>(
      'guest-passes/',
      body
    ).then();
  }

  removeGuestPass(guestPass: GuestPass): Promise<void> {
    return this.apiService.deleteFromAPI<any>(
      guestPass.url
    ).then();
  }

  getCustomerSavedPaymentMethods(customerId: number,
                                 paymentMethodType?: CustomerPaymentMethodType,
                                 connId?: number): Promise<CustomerPaymentMethod[]> {
    let params = new HttpParams();
    if (paymentMethodType) {
      params = params.set('payment_method_type', paymentMethodType)
    }
    if (connId) {
      params = params.set('payment_processor_connection_id', connId.toString())
    }

    return this.apiService.getFromAPI<CustomerPaymentMethod[]>(
      'customers/' + customerId.toString() + '/saved-payment-methods/',
      params
    );
  }

  findCustomersWithoutDocument(customerIds: number[],
                               documentTemplateId: number): Promise<number[]> {
    let params = new HttpParams({encoder: new HttpUrlEncodingCodec() });
    params = params.set('customer_ids', customerIds.join(','));
    params = params.set('document_template_id', documentTemplateId.toString());

    return this.apiService.getFromAPI<number[]>(
      'find-customers-without-document/',
      params
    );
  }

  findCustomersWithWaiverIssues(customerIds: number[],
                                locationId: number,
                                eventStartDate: string): Promise<{ [customerId: string]: string }> {
    let params = new HttpParams({encoder: new HttpUrlEncodingCodec() });
    params = params.set('customer_ids', customerIds.join(','));
    params = params.set('location_id', locationId.toString());
    params = params.set('event_start_date', eventStartDate);

    return this.apiService.getFromAPI<{ [customerId: string]: string }>(
      'find-customers-with-waiver-issues/',
      params
    );
  }
}
